/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Coach Service - Handles all coach-related API calls
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import api from './api'
import loggingService from './loggingService'

/**
 * Extract user-friendly error message from API error
 * @param {Error} error - Axios error object
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} User-friendly error message
 */
const extractErrorMessage = (error, fallback) => {
  return error.response?.data?.message || error.response?.data?.error || fallback
}

class CoachService {
  // ===== COACH DASHBOARD METHODS =====

  /**
   * Get dashboard statistics for the coach
   * @returns {Promise<Object>} Dashboard stats
   */
  async getStats() {
    try {
      const response = await api.get('/coach/stats')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch coach stats')
      loggingService.error('Error fetching coach stats', { message })
      throw new Error(message)
    }
  }

  /**
   * Get all clients for the authenticated coach
   * @param {boolean} includeStats - Whether to include statistics for each client
   * @returns {Promise<Object>} List of clients
   */
  async getClients(includeStats = false) {
    try {
      const response = await api.get('/coach/clients', {
        params: { includeStats }
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch clients')
      loggingService.error('Error fetching coach clients', { message })
      throw new Error(message)
    }
  }

  /**
   * Get pending client requests (invitations sent by coach)
   * @returns {Promise<Object>} List of pending requests
   */
  async getPendingRequests() {
    try {
      const response = await api.get('/coach/pending-requests')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch pending requests')
      loggingService.error('Error fetching pending requests', { message })
      throw new Error(message)
    }
  }

  /**
   * Get relationships that have been accepted but are awaiting tutoring agreements
   * (status pending_agreements). Each entry includes a `readiness` breakdown of what is outstanding.
   * @returns {Promise<Object>} List of pending-agreement relationships
   */
  async getPendingAgreements() {
    try {
      const response = await api.get('/coach/pending-agreements')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch pending agreements')
      loggingService.error('Error fetching pending agreements', { message })
      throw new Error(message)
    }
  }

  /**
   * Cancel a pending invitation sent by coach
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} Cancellation result
   */
  async cancelPendingRequest(relationshipId) {
    try {
      const response = await api.delete(`/coach/pending-requests/${relationshipId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to cancel invitation')
      loggingService.error('Error cancelling invitation', { message })
      throw new Error(message)
    }
  }

  /**
   * Get invitations received from users (users who want this coach)
   * @returns {Promise<Object>} List of received invitations
   */
  async getReceivedInvitations() {
    try {
      const response = await api.get('/coach/received-invitations')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch received invitations')
      loggingService.error('Error fetching received invitations', { message })
      throw new Error(message)
    }
  }

  /**
   * Accept an invitation from a user
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} Acceptance result
   */
  async acceptReceivedInvitation(relationshipId) {
    try {
      const response = await api.post(`/coach/received-invitations/${relationshipId}/accept`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to accept invitation')
      loggingService.error('Error accepting invitation', { message })
      throw new Error(message)
    }
  }

  /**
   * Decline an invitation from a user
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} Decline result
   */
  async declineReceivedInvitation(relationshipId) {
    try {
      const response = await api.post(`/coach/received-invitations/${relationshipId}/decline`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to decline invitation')
      loggingService.error('Error declining invitation', { message })
      throw new Error(message)
    }
  }

  /**
   * Get detailed information for a specific client
   * @param {string} clientId - Client user ID
   * @returns {Promise<Object>} Client details with stats
   */
  async getClient(clientId) {
    try {
      const response = await api.get(`/coach/clients/${clientId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch client details')
      loggingService.error('Error fetching client details', { message })
      throw new Error(message)
    }
  }

  /**
   * Get statistics for a specific client
   * @param {string} clientId - Client user ID
   * @returns {Promise<Object>} Client statistics
   */
  async getClientStats(clientId) {
    try {
      const response = await api.get(`/coach/clients/${clientId}/stats`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch client stats')
      loggingService.error('Error fetching client stats', { message })
      throw new Error(message)
    }
  }

  /**
   * Get all challenges for a specific client
   * @param {string} clientId - Client user ID
   * @param {number} limit - Number of challenges to fetch
   * @param {number} offset - Pagination offset
   * @returns {Promise<Object>} Challenges with pagination
   */
  async getClientChallenges(clientId, limit = 20, offset = 0) {
    try {
      const response = await api.get(`/coach/clients/${clientId}/challenges`, {
        params: { limit, offset }
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch client challenges')
      loggingService.error('Error fetching client challenges', { message })
      throw new Error(message)
    }
  }

  /**
   * Get detailed information for a specific challenge
   * @param {string} clientId - Client user ID
   * @param {string} challengeId - Challenge ID
   * @returns {Promise<Object>} Challenge details
   */
  async getClientChallenge(clientId, challengeId) {
    try {
      const response = await api.get(`/coach/clients/${clientId}/challenges/${challengeId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch challenge details')
      loggingService.error('Error fetching challenge details', { message })
      throw new Error(message)
    }
  }

  /**
   * Invite a user to become a client
   * @param {string} email - Client email
   * @param {string} organizationId - Organization ID
   * @param {Object} permissions - Optional custom permissions
   * @returns {Promise<Object>} Created relationship
   */
  async inviteClient(email, organizationId, permissions = null) {
    try {
      const response = await api.post('/coach/invite-client', {
        email,
        organizationId,
        permissions
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to invite client')
      loggingService.error('Error inviting client', { message })
      throw new Error(message)
    }
  }

  /**
   * Remove a client relationship
   * @param {string} clientId - Client user ID
   * @returns {Promise<Object>} Removal result
   */
  async removeClient(clientId) {
    try {
      const response = await api.delete(`/coach/clients/${clientId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to remove client')
      loggingService.error('Error removing client', { message })
      throw new Error(message)
    }
  }

  /**
   * Update permissions for a client relationship
   * @param {string} clientId - Client user ID
   * @param {Object} permissions - New permissions object
   * @returns {Promise<Object>} Updated permissions
   */
  async updateClientPermissions(clientId, permissions) {
    try {
      const response = await api.patch(`/coach/clients/${clientId}/permissions`, {
        permissions
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to update permissions')
      loggingService.error('Error updating permissions', { message })
      throw new Error(message)
    }
  }

  // ===== USER-FACING COACH METHODS (for clients to manage their coaches) =====

  /**
   * Get all coaches for the authenticated user
   * @returns {Promise<Object>} List of coaches
   */
  async getMyCoaches() {
    try {
      const response = await api.get('/user/coaches')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch your coaches')
      loggingService.error('Error fetching user coaches', { message })
      throw new Error(message)
    }
  }

  /**
   * Get pending coach invitations for the authenticated user
   * @returns {Promise<Object>} List of pending invitations
   */
  async getCoachInvitations() {
    try {
      const response = await api.get('/user/coach-invitations')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch coach invitations')
      loggingService.error('Error fetching coach invitations', { message })
      throw new Error(message)
    }
  }

  /**
   * Invite a coach to work with the user
   * @param {string} email - Coach email
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Created relationship
   */
  async inviteCoach(email, organizationId) {
    try {
      const response = await api.post('/user/invite-coach', {
        email,
        organizationId
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to invite coach')
      loggingService.error('Error inviting coach', { message })
      throw new Error(message)
    }
  }

  /**
   * Accept a coach invitation
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} Acceptance result
   */
  async acceptCoachInvitation(relationshipId) {
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/accept`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to accept coach invitation')
      loggingService.error('Error accepting coach invitation', { message })
      throw new Error(message)
    }
  }

  /**
   * Decline a coach invitation
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} Decline result
   */
  async declineCoachInvitation(relationshipId) {
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/decline`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to decline coach invitation')
      loggingService.error('Error declining coach invitation', { message })
      throw new Error(message)
    }
  }

  /**
   * Re-notify the student's linked guardian(s) about a coaching request awaiting guardian approval.
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} { success, remindedCount, guardianEmailsMasked, message }
   */
  async remindGuardian(relationshipId) {
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/remind-guardian`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to send guardian reminder')
      loggingService.error('Error reminding guardian', { message })
      throw new Error(message)
    }
  }

  /**
   * Resend a pending family invitation (e.g. a guardian invite whose first email never arrived).
   * Rotates the token + extends expiry server-side; does not change the email.
   * @param {string} invitationId - OrganizationInvitation ID
   * @returns {Promise<Object>} { success, invitation, message }
   */
  async resendInvitation(invitationId) {
    try {
      const response = await api.post(`/family/invitations/${invitationId}/resend`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to resend invitation')
      loggingService.error('Error resending invitation', { message })
      throw new Error(message)
    }
  }

  /**
   * Remove a coach relationship
   * @param {string} coachId - Coach user ID
   * @returns {Promise<Object>} Removal result
   */
  async removeCoach(coachId) {
    try {
      const response = await api.delete(`/user/coaches/${coachId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to remove coach')
      loggingService.error('Error removing coach', { message })
      throw new Error(message)
    }
  }

  /**
   * Update permissions for a coach (controlled by the student/client)
   * This allows students to control what their coaches can see
   * @param {string} coachId - Coach user ID
   * @param {Object} permissions - New permissions object
   * @returns {Promise<Object>} Updated permissions
   */
  async updateMyCoachPermissions(coachId, permissions) {
    try {
      const response = await api.patch(`/user/coaches/${coachId}/permissions`, {
        permissions
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to update coach permissions')
      loggingService.error('Error updating coach permissions', { message })
      throw new Error(message)
    }
  }

  // ===== GUARDIAN COACH MANAGEMENT (for guardians managing coaches for their children) =====

  /**
   * Get pending coach requests for guardian's children
   * @returns {Promise<Object>} List of pending coach requests
   */
  async getGuardianCoachApprovals() {
    try {
      const response = await api.get('/family/coach-approvals')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch coach approvals')
      loggingService.error('Error fetching coach approvals', { message })
      throw new Error(message)
    }
  }

  /**
   * Get children's coach relationships awaiting tutoring agreements (pending_agreements),
   * with a readiness breakdown so the guardian can sign their consent.
   * @returns {Promise<Object>} List of pending-agreement relationships for the guardian's children
   */
  async getGuardianCoachAgreements() {
    try {
      const response = await api.get('/family/coach-agreements')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch coach agreements')
      loggingService.error('Error fetching coach agreements', { message })
      throw new Error(message)
    }
  }

  /**
   * Approve a coach for a child
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} Approval result
   */
  async approveCoachForChild(relationshipId) {
    try {
      const response = await api.post(`/family/coach-approvals/${relationshipId}/accept`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to approve coach')
      loggingService.error('Error approving coach', { message })
      throw new Error(message)
    }
  }

  /**
   * Decline a coach for a child
   * @param {string} relationshipId - Relationship ID
   * @returns {Promise<Object>} Decline result
   */
  async declineCoachForChild(relationshipId) {
    try {
      const response = await api.post(`/family/coach-approvals/${relationshipId}/decline`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to decline coach')
      loggingService.error('Error declining coach', { message })
      throw new Error(message)
    }
  }

  /**
   * Get all coaches for a specific child
   * @param {string} childId - Child user ID
   * @returns {Promise<Object>} List of coaches
   */
  async getChildCoaches(childId) {
    try {
      const response = await api.get(`/family/children/${childId}/coaches`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch child coaches')
      loggingService.error('Error fetching child coaches', { message })
      throw new Error(message)
    }
  }

  /**
   * Remove a coach from a child
   * @param {string} childId - Child user ID
   * @param {string} coachId - Coach user ID
   * @returns {Promise<Object>} Removal result
   */
  async removeCoachFromChild(childId, coachId) {
    try {
      const response = await api.delete(`/family/children/${childId}/coaches/${coachId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to remove coach from child')
      loggingService.error('Error removing coach from child', { message })
      throw new Error(message)
    }
  }

  // ===== COACH FEEDBACK METHODS =====

  /**
   * Create or update feedback on a challenge
   * @param {string} clientId - Student user ID
   * @param {string} challengeId - Challenge ID
   * @param {string} content - Feedback text
   * @returns {Promise<Object>} Created feedback
   */
  async createFeedback(clientId, challengeId, content) {
    try {
      const response = await api.post(
        `/coach/clients/${clientId}/challenges/${challengeId}/feedback`,
        {
          content
        }
      )
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to create feedback')
      loggingService.error('Error creating feedback', { message })
      throw new Error(message)
    }
  }

  /**
   * Get coach's feedback on a challenge
   * @param {string} clientId - Student user ID
   * @param {string} challengeId - Challenge ID
   * @returns {Promise<Object>} Feedback or null
   */
  async getFeedback(clientId, challengeId) {
    try {
      // A viewer who is a coach in general but not THIS student's coach (e.g. a guardian who is
      // also a coach) gets an expected 403 here, which callers handle locally. Opt out of the
      // global 403 toast/modal so it doesn't escalate that handled case into a blocking error.
      const response = await api.get(
        `/coach/clients/${clientId}/challenges/${challengeId}/feedback`,
        { suppressGlobal403: true }
      )
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get feedback')
      loggingService.error('Error getting feedback', { message })
      throw new Error(message)
    }
  }

  /**
   * Get the coach feedback left for a guardian's child on a challenge (read-only guardian view).
   * @param {string} childId - The child (student) user ID
   * @param {string} challengeId - Challenge ID
   * @returns {Promise<Object>} { success, feedback: [...] }
   */
  async getChildFeedback(childId, challengeId) {
    try {
      // A viewer who isn't this child's guardian gets an expected 403, handled locally by the
      // caller; opt out of the global 403 toast/modal.
      const response = await api.get(
        `/family/children/${childId}/challenges/${challengeId}/feedback`,
        { suppressGlobal403: true }
      )
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get feedback')
      loggingService.error('Error getting child feedback', { message })
      throw new Error(message)
    }
  }

  /**
   * Update feedback
   * @param {string} feedbackId - Feedback ID
   * @param {string} content - New content
   * @returns {Promise<Object>} Updated feedback
   */
  async updateFeedback(feedbackId, content) {
    try {
      const response = await api.patch(`/coach/feedback/${feedbackId}`, { content })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to update feedback')
      loggingService.error('Error updating feedback', { message })
      throw new Error(message)
    }
  }

  /**
   * Delete feedback
   * @param {string} feedbackId - Feedback ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFeedback(feedbackId) {
    try {
      const response = await api.delete(`/coach/feedback/${feedbackId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to delete feedback')
      loggingService.error('Error deleting feedback', { message })
      throw new Error(message)
    }
  }

  // ===== STUDENT FEEDBACK METHODS =====

  /**
   * Get all coach feedback for the student
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Feedback list with unread count
   */
  async getMyFeedback({ unreadOnly = false, limit = 20, offset = 0 } = {}) {
    try {
      const response = await api.get('/user/feedback', {
        params: { unreadOnly, limit, offset }
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get student feedback')
      loggingService.error('Error getting student feedback', { message })
      throw new Error(message)
    }
  }

  /**
   * Get unread feedback count for dashboard badge
   * @returns {Promise<Object>} Unread count
   */
  async getUnreadFeedbackCount() {
    try {
      const response = await api.get('/user/feedback/count')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get feedback count')
      loggingService.error('Error getting feedback count', { message })
      throw new Error(message)
    }
  }

  /**
   * Mark feedback as read
   * @param {string} feedbackId - Feedback ID
   * @returns {Promise<Object>} Updated feedback
   */
  async markFeedbackAsRead(feedbackId) {
    try {
      const response = await api.patch(`/user/feedback/${feedbackId}/read`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to mark feedback as read')
      loggingService.error('Error marking feedback as read', { message })
      throw new Error(message)
    }
  }

  /**
   * Mark all feedback as read
   * @returns {Promise<Object>} Count of marked feedback
   */
  async markAllFeedbackAsRead() {
    try {
      const response = await api.post('/user/feedback/mark-all-read')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to mark all feedback as read')
      loggingService.error('Error marking all feedback as read', { message })
      throw new Error(message)
    }
  }

  // ===== COACH ANNOUNCEMENT METHODS =====

  /**
   * Create a new announcement (coach)
   * @param {Object} data - Announcement data
   * @param {string} data.title - Announcement title
   * @param {string} data.content - Announcement content
   * @param {string} [data.audience='all'] - Target audience
   * @param {string[]} [data.targetIds] - Specific student IDs
   * @param {string} [data.expiresAt] - Expiration date
   * @returns {Promise<Object>} Created announcement
   */
  async createAnnouncement(data) {
    try {
      const response = await api.post('/coach/announcements', data)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to create announcement')
      loggingService.error('Error creating announcement', { message })
      throw new Error(message)
    }
  }

  /**
   * Get coach's announcements
   * @param {Object} options - Query options
   * @param {number} [options.limit=20] - Max results
   * @param {number} [options.offset=0] - Offset
   * @returns {Promise<Object>} List of announcements
   */
  async getAnnouncements({ limit = 20, offset = 0 } = {}) {
    try {
      const response = await api.get('/coach/announcements', { params: { limit, offset } })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get announcements')
      loggingService.error('Error getting announcements', { message })
      throw new Error(message)
    }
  }

  /**
   * Get a specific announcement with read details
   * @param {string} announcementId - Announcement ID
   * @returns {Promise<Object>} Announcement details
   */
  async getAnnouncement(announcementId) {
    try {
      const response = await api.get(`/coach/announcements/${announcementId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get announcement')
      loggingService.error('Error getting announcement', { message })
      throw new Error(message)
    }
  }

  /**
   * Update an announcement
   * @param {string} announcementId - Announcement ID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>} Updated announcement
   */
  async updateAnnouncement(announcementId, data) {
    try {
      const response = await api.patch(`/coach/announcements/${announcementId}`, data)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to update announcement')
      loggingService.error('Error updating announcement', { message })
      throw new Error(message)
    }
  }

  /**
   * Delete an announcement
   * @param {string} announcementId - Announcement ID
   * @returns {Promise<Object>} Success response
   */
  async deleteAnnouncement(announcementId) {
    try {
      const response = await api.delete(`/coach/announcements/${announcementId}`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to delete announcement')
      loggingService.error('Error deleting announcement', { message })
      throw new Error(message)
    }
  }

  /**
   * Send announcement to a specific client
   * @param {string} clientId - Client user ID
   * @param {Object} data - Announcement data
   * @returns {Promise<Object>} Created announcement
   */
  async sendAnnouncementToClient(clientId, data) {
    try {
      const response = await api.post(`/coach/clients/${clientId}/announcements`, data)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to send announcement to client')
      loggingService.error('Error sending announcement to client', { message })
      throw new Error(message)
    }
  }

  // ===== STUDENT ANNOUNCEMENT METHODS =====

  /**
   * Get announcements from coaches (student)
   * @param {Object} options - Query options
   * @param {number} [options.limit=20] - Max results
   * @param {number} [options.offset=0] - Offset
   * @param {boolean} [options.unreadOnly=false] - Only unread
   * @returns {Promise<Object>} List of announcements
   */
  async getMyAnnouncements({ limit = 20, offset = 0, unreadOnly = false } = {}) {
    try {
      const response = await api.get('/user/coach-announcements', {
        params: { limit, offset, unreadOnly }
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get announcements')
      loggingService.error('Error getting announcements', { message })
      throw new Error(message)
    }
  }

  /**
   * Get unread announcement count (student)
   * @returns {Promise<Object>} Unread count
   */
  async getUnreadAnnouncementCount() {
    try {
      const response = await api.get('/user/coach-announcements/count')
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to get unread announcement count')
      loggingService.error('Error getting unread announcement count', { message })
      throw new Error(message)
    }
  }

  /**
   * Mark an announcement as read (student)
   * @param {string} announcementId - Announcement ID
   * @returns {Promise<Object>} Success response
   */
  async markAnnouncementAsRead(announcementId) {
    try {
      const response = await api.patch(`/user/coach-announcements/${announcementId}/read`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to mark announcement as read')
      loggingService.error('Error marking announcement as read', { message })
      throw new Error(message)
    }
  }
}

export default new CoachService()
